import { DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useContext, useState } from 'react';

import {
  EquipmentType,
  EquipmentTypeValues,
} from '@/domain/impact-co2/entities/question.entity';
import { VenueDefaultDataEquipment } from '@/domain/project-venue/entities/project-venue.entity';
import { VenuePreset } from '@/domain/project-venue-presets/entities/project-venue-presets.entity.ts';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { thStyle } from '@/ui/components/table/Table.utils';
import { translate } from '@/ui/i18n';
import { DefaultDatasContext } from '@/ui/pages/app/project/venue/default-datas/default-datas-quiz-panel/DefaultDatasContext';
import {
  QuizEdition,
  QuizNotEdition,
} from '@/ui/pages/app/project/venue/default-datas/default-datas-quiz-panel/DefaultDatasQuizComponents';
import { ColorsRaw } from '@/ui/theme/colors-raw';

export function EquipmentForm({
  onEdit,
  venueDefaultDatas,
  forceEdit,
  onClose,
}: {
  onEdit(venueDatas: VenuePreset): void;
  venueDefaultDatas: VenuePreset;
  forceEdit?: boolean;
  onClose?: () => void;
}) {
  const { panelOpen } = useContext(DefaultDatasContext);
  const [isEditing, setIsEditing] = useState(forceEdit ? true : false);
  const [values, setValues] = useState(
    structuredClone(venueDefaultDatas.equipment),
  );

  const disableSubmitActions = false; //TODO

  const addEquipmentToAnswer = (equipment: EquipmentType) => {
    setValues([...values, { type: equipment, quantity: 0 }]);
  };

  const updateEquipmentQuantity = (value: string, index: number) => {
    const newValues = [...values];
    newValues[index].quantity = Number(value);
    if (!isNaN(newValues[index].quantity)) {
      setValues(newValues);
    }
  };

  const removeEquipment = (index: number) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    setValues(newValues);
  };

  const handleEdit = () => {
    onEdit({ ...venueDefaultDatas, equipment: values });
    if (!forceEdit) setIsEditing(false);
  };

  const handleRemoveClick = () => {
    if (onClose) onClose();
    else setIsEditing(false);
  };

  return (
    <div id="equipment-form">
      {(isEditing && !!panelOpen) || !!forceEdit ? (
        <QuizEdition
          removeFromEditing={handleRemoveClick}
          title={translate(
            `ProjectsVenue.DefaultDatas.Public.Forms.Equipment.what-equipment`,
          )}
          form={
            <Form
              onSubmit={handleEdit}
              disableSubmit={disableSubmitActions}
              addEquipmentToAnswer={addEquipmentToAnswer}
              updateEquipmentQuantity={updateEquipmentQuantity}
              removeEquipment={removeEquipment}
              values={values}
            />
          }
        />
      ) : (
        <QuizNotEdition
          addToEditing={() => setIsEditing(true)}
          answeredValues={values.map((value) => {
            return `${translate(`ProjectsVenue.DefaultDatas.Public.Forms.Equipment.EquipmentTypeValues.${value.type}`)}: ${value.quantity}`;
          })}
          title={translate(
            `ProjectsVenue.DefaultDatas.Public.Forms.Equipment.what-equipment`,
          )}
          isAnswered={values.length > 0}
        />
      )}
    </div>
  );
}

function Form({
  addEquipmentToAnswer,
  updateEquipmentQuantity,
  removeEquipment,
  disableSubmit,
  onSubmit,
  values,
}: {
  onSubmit: () => void;
  addEquipmentToAnswer: (equipment: EquipmentType) => void;
  disableSubmit: boolean;
  updateEquipmentQuantity: (value: string, index: number) => void;
  removeEquipment: (index: number) => void;
  values: VenueDefaultDataEquipment;
}) {
  const [selectedEquipment, setSelectedEquipment] = useState<
    EquipmentType | ''
  >('');

  const getSelectValues = () => {
    const selectedEquipments = values.map((value) => value.type);

    return selectedEquipments
      ? EquipmentTypeValues.filter((acc) => !selectedEquipments.includes(acc))
      : EquipmentTypeValues;
  };

  const addEquipment = () => {
    if (selectedEquipment) {
      addEquipmentToAnswer(selectedEquipment);
      setSelectedEquipment('');
    }
  };

  const handleChangeQuantity = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    updateEquipmentQuantity(e.target.value, index);
  };

  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <Stack>
      <Table align="right">
        <TableHead>
          <TableRow>
            <TableCell style={thStyle}>
              {translate(
                `ProjectsVenue.DefaultDatas.Public.Forms.Equipment.equipment-type`,
              )}
            </TableCell>
            <TableCell style={thStyle}>
              {`${translate(
                `ProjectsVenue.DefaultDatas.Public.Forms.Equipment.quantity`,
              )}`}
            </TableCell>
            <TableCell style={thStyle} />
          </TableRow>
        </TableHead>
        <TableBody>
          {values?.map((value, index) => (
            <TableRow key={'selectedTransport' + index}>
              <TableCell>
                {translate(
                  `ProjectsVenue.DefaultDatas.Public.Forms.Equipment.EquipmentTypeValues.${value.type}`,
                )}
              </TableCell>
              <TableCell sx={{ py: 0 }}>
                <Box display="flex" alignItems="center">
                  <FSTextField
                    sx={{ width: '80px' }}
                    type="number"
                    textAlign="right"
                    value={value.quantity}
                    onChange={(e) => handleChangeQuantity(e, index)}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ px: 0 }}>
                <Box display="flex" justifyContent="end">
                  <Button onClick={() => removeEquipment(index)}>
                    <DeleteOutline sx={{ color: ColorsRaw.basic.White }} />
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={4}>
              <Box display={'flex'} alignItems={'center'}>
                <FSTextField
                  select
                  label={translate(
                    'ProjectsVenue.DefaultDatas.Public.Forms.Equipment.equipment-type',
                  )}
                  value={selectedEquipment}
                  onChange={(e) =>
                    setSelectedEquipment(e.target.value as EquipmentType)
                  }
                  sx={{ width: '100%' }}
                >
                  {getSelectValues().map((value) => (
                    <MenuItem key={value} value={value}>
                      {translate(
                        `ProjectsVenue.DefaultDatas.Public.Forms.Equipment.EquipmentTypeValues.${value}`,
                      )}
                    </MenuItem>
                  ))}
                </FSTextField>

                <FSPrimaryButton sx={{ ml: 1 }} onClick={addEquipment}>
                  {translate('Form.add')}
                </FSPrimaryButton>
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box marginTop={2}>
        <FSPrimaryButton
          type="submit"
          disabled={disableSubmit}
          onClick={handleSubmit}
        >
          {translate('Form.save')}
        </FSPrimaryButton>
      </Box>
    </Stack>
  );
}
